// form
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import _isEqual from 'lodash/isEqual';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { FormProvider as Form, type UseFormReturn } from 'react-hook-form';

import { ACTION_SIDEBAR_ID } from 'components/molecules/LexicalEditor/plugins/ActionPicker/constants';
import { isCreatingAction } from 'components/molecules/LexicalEditor/plugins/ActionPicker/utils';
import { useViewMode } from 'screens/withPermissions/useViewMode';
import { ACTION_MENU_ID } from '../LexicalEditor/plugins/ActionPicker/constants';

type TSubmitData<A, B> = B extends undefined ? A : B;

type BaseProps<A extends FieldValues, B extends FieldValues | undefined> = {
  className?: string;
  children: React.ReactNode;
  methods: UseFormReturn<A, any, B>;
  onSubmit?: SubmitHandler<TSubmitData<A, B>>;
  onBlur?: SubmitHandler<TSubmitData<A, B>>;
  id?: string;
};

// We should consider if we want onBlur and onSubmit to coexist in the same time.
type Props<A extends FieldValues, B extends FieldValues | undefined> =
  | (BaseProps<A, B> & {
      onSubmit: SubmitHandler<TSubmitData<A, B>>;
      defaultValues?: A;
      allowSubmit?: boolean;
    })
  | (BaseProps<A, B> & {
      onSubmit?: undefined;
      defaultValues: A;
      allowSubmit?: undefined;
    });

/**
 * A provider component to manage form state and submission.
 * @link Here is the notion doc that explains usage of FormProvider and FormInput https://www.notion.so/polyai/a21122f6200f4be3a49ff9ef78480596?v=21823515e11b4271b1cc3dbda81eecc1&p=9293a19d22044aa6bf932af571683ea1&pm=s
 * @param {Props} id - This value is used with button that will have same the value of "form" prop. This is only needed if submit button needs to live outside the form. If button lives in the <form> tag this can be ignored.
 * @return {React.JSX.Element} The rendered form provider component.
 */
export default function FormProvider<
  A extends FieldValues,
  B extends FieldValues | undefined,
>({
  className,
  children,
  onSubmit,
  onBlur,
  methods,
  id,
  defaultValues,
  allowSubmit,
}: Props<A, B>) {
  const ref = useRef<HTMLFormElement>(null);
  const [oldDefaultValues, setOldDefaultValues] = useState(defaultValues);
  const { handleSubmit } = methods;
  const { isEditable } = useViewMode();

  const {
    reset,
    getValues,
    formState: { isDirty, isSubmitSuccessful, errors },
  } = methods;

  const handleBlur = (event: React.FocusEvent<HTMLFormElement>) => {
    if (!onBlur || !isEditable) {
      return;
    }
    if (!isDirty) {
      return;
    }

    setTimeout(() => {
      // Check the newly focused element in the next tick of the event loop
      if (
        ref.current &&
        !ref.current.contains(event.relatedTarget as Node) &&
        // We have to do it like this as clicking on pop up submits the form
        !document
          .getElementById('popup-root')
          ?.contains(document.activeElement) &&
        !document
          .getElementById(ACTION_MENU_ID)
          ?.contains(document.activeElement) &&
        // Always allow onBlur submit when in action sidebar
        (document.getElementById(ACTION_SIDEBAR_ID) || !isCreatingAction())
      ) {
        // This is not ideal, but the way we declared onSubmit prevents TS from inferring this properly, but in this case we do know more than TS
        // @ts-ignore
        void handleSubmit(onBlur, validationErrors => {
          console.error('Form validation errors on blur:', validationErrors);
          console.error('Form state errors:', errors);
        })();
      }
    }, 0);
  };

  // RHF does not reset isDirty field when form is successfully submitted so we have to do that manually
  // https://github.com/react-hook-form/react-hook-form/issues/3097
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
    }
  }, [isSubmitSuccessful, reset, getValues]);

  // This resets the form when the draft is deleted
  useEffect(() => {
    if (!_isEqual(oldDefaultValues, defaultValues)) {
      reset(defaultValues);
      setOldDefaultValues(defaultValues);
    }
  }, [defaultValues, reset, oldDefaultValues]);

  return (
    <Form {...methods}>
      <form
        ref={ref}
        className={className}
        id={id}
        onBlur={handleBlur}
        onSubmit={e => {
          e.preventDefault();
          if (onSubmit && (allowSubmit || isEditable)) {
            // This is not ideal, but the way we declared onSubmit prevents TS from inferring this properly, but in this case we do know more than TS
            // @ts-ignore
            void handleSubmit(onSubmit)();
          }
        }}
      >
        {children}
      </form>
    </Form>
  );
}
